// w06 Sumalak — Persian/Timurid-inspired wedding catalog card.
//
// Paper-invitation feel: an outer emerald foil mat wraps a cream inner
// card; the focal medallion is a pomegranate-and-laurel manuscript roundel
// (or photo if provided). Names sit in Cinzel caps; the caption pairs
// "TO'Y MAROSIMI" with date and venue.
import { Photo, type LayoutCtx } from "./_shared";

export function MiniatureLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
}: LayoutCtx) {
  // Emerald + parchment foil for the outer mat.
  const foil = `linear-gradient(135deg, ${accent} 0%, color-mix(in srgb, ${accent} 55%, ${bg} 45%) 50%, ${accent} 100%)`;
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: foil,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          position: "relative",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          TO'Y MAROSIMI
        </div>

        {/* Pomegranate manuscript medallion. */}
        <div
          style={{
            width: "60%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
            borderRadius: "50%",
            background: `radial-gradient(circle at 50% 50%, ${bg} 0%, ${accent}10 60%, ${bg})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `inset 0 0 0 1px ${accent}66, 0 0 0 2px ${bg}, 0 0 0 3px ${accent}33`,
            overflow: "hidden",
          }}
        >
          <Photo
            src={photo}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", borderRadius: "50%" }}
            fallback={
              <svg viewBox="0 0 100 100" style={{ width: "82%", height: "82%" }} aria-hidden>
                {/* Concentric manuscript rings */}
                <circle cx="50" cy="50" r="40" fill="none" stroke={accent} strokeWidth="0.4" opacity="0.5" />
                <circle cx="50" cy="50" r="34" fill="none" stroke={accent} strokeWidth="0.4" opacity="0.4" />

                {/* Pomegranate (central motif). */}
                <g transform="translate(50 54)">
                  <path
                    d="M0 -18 Q14 -12 14 4 Q14 18 0 22 Q-14 18 -14 4 Q-14 -12 0 -18 Z"
                    fill={accent}
                    opacity="0.85"
                  />
                  {/* Crown */}
                  <path d="M-3 -19 L0 -23 L3 -19 L1 -15 L-1 -15 Z" fill={accent} />
                  {/* Seeds */}
                  {[
                    [-5, -4], [0, -4], [5, -4],
                    [-7, 2], [-2, 2], [3, 2], [8, 2],
                    [-5, 8], [0, 8], [5, 8],
                    [-2, 14], [3, 14],
                  ].map(([x, y], i) => (
                    <circle key={i} cx={x} cy={y} r="1.1" fill={bg} opacity="0.85" />
                  ))}
                </g>

                {/* Laurel half-wreaths */}
                {[-1, 1].map((side) => (
                  <g key={side} transform={`translate(50 50) scale(${side} 1)`}>
                    {[0, 1, 2, 3].map((i) => {
                      const a = -50 + i * 25;
                      return (
                        <g key={i} transform={`rotate(${a}) translate(0 -32)`}>
                          <ellipse cx="0" cy="0" rx="3.6" ry="1.4" fill={accent} opacity="0.7" />
                        </g>
                      );
                    })}
                  </g>
                ))}
              </svg>
            }
          />
        </div>

        {/* Names — Cinzel caps. */}
        <div
          style={{
            fontFamily: font,
            fontSize: 22,
            letterSpacing: "0.12em",
            lineHeight: 1.15,
            fontWeight: 500,
            color: ink,
            textTransform: "uppercase",
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          TO'Y MAROSIMI · {date.toUpperCase()}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
